import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const LOCS = ["Beverly Hills","Santa Monica","West Hollywood"];

const inp = {width:"100%",padding:"8px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export function HolidayHoursEditor() {
  const [dates, setDates] = useState([]);
  const [form, setForm] = useState({ date:"", mode:"closed", start_time:"10:00", end_time:"15:00", note:"" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { load(); }, []);
  const load = async () => {
    const { data } = await supabase.from("booking_blocked_dates").select("*").order("date");
    setDates(data || []);
  };

  const add = async () => {
    if (!form.date) { setMsg("Pick a date"); return; }
    if (form.mode === "hours" && (!form.start_time || !form.end_time)) { setMsg("Set open & close times"); return; }
    setSaving(true); setMsg("");
    const row = {
      date: form.date,
      closed: form.mode === "closed",
      start_time: form.mode === "hours" ? form.start_time : null,
      end_time: form.mode === "hours" ? form.end_time : null,
      note: form.note?.trim() || null,
    };
    // upsert on date so re-adding the same date updates it instead of erroring
    const { error } = await supabase.from("booking_blocked_dates").upsert(row, { onConflict: "date" });
    setSaving(false);
    if (error) { setMsg("Failed: " + error.message); return; }
    setForm({ date:"", mode:"closed", start_time:"10:00", end_time:"15:00", note:"" });
    setMsg("Saved ✓"); setTimeout(()=>setMsg(""), 2000);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this date override?")) return;
    await supabase.from("booking_blocked_dates").delete().eq("id", id);
    load();
  };

  const fmtDate = (d) => { try { return new Date(d + "T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric",year:"numeric"}); } catch { return d; } };

  return (
    <div>
      <div style={{marginBottom:14}}>
        <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Holidays & Special Hours</div>
        <div style={{fontSize:11,color:C.muted}}>Close the spa on a date, or set special open/close hours for it. Applies to all providers and the public booking page.</div>
      </div>
      {msg && <div style={{padding:"8px 12px",background:C.greenL,color:C.green,borderRadius:7,fontSize:12,fontWeight:600,marginBottom:12,border:"1px solid "+C.green+"40"}}>{msg}</div>}

      <div style={{background:C.light,border:"1px solid "+C.border,borderRadius:10,padding:14,marginBottom:16,display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
        <div>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:4}}>Date</label>
          <input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} style={{...inp,width:160}}/>
        </div>
        <div>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:4}}>Type</label>
          <select value={form.mode} onChange={e=>setForm({...form,mode:e.target.value})} style={{...inp,width:150}}>
            <option value="closed">Closed (holiday)</option>
            <option value="hours">Special hours</option>
          </select>
        </div>
        {form.mode === "hours" && (<>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:4}}>Open</label>
            <input type="time" value={form.start_time} onChange={e=>setForm({...form,start_time:e.target.value})} style={{...inp,width:120}}/>
          </div>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:4}}>Close</label>
            <input type="time" value={form.end_time} onChange={e=>setForm({...form,end_time:e.target.value})} style={{...inp,width:120}}/>
          </div>
        </>)}
        <div style={{flex:1,minWidth:140}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:4}}>Note</label>
          <input value={form.note} onChange={e=>setForm({...form,note:e.target.value})} placeholder="e.g. July 4th" style={inp}/>
        </div>
        <button onClick={add} disabled={saving} style={{padding:"9px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F,opacity:saving?0.6:1,whiteSpace:"nowrap"}}>{saving?"Saving…":"+ Add"}</button>
      </div>

      {dates.length === 0 ? (
        <div style={{padding:24,textAlign:"center",color:C.muted,fontSize:12,background:C.light,borderRadius:8,border:"1px dashed "+C.border}}>No holidays or special hours set</div>
      ) : (
        <div style={{border:"1px solid "+C.border,borderRadius:10,overflow:"hidden"}}>
          {dates.map(d => (
            <div key={d.id} style={{padding:"11px 14px",borderBottom:"1px solid "+C.border,display:"flex",alignItems:"center",gap:12}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>{fmtDate(d.date)}</div>
                <div style={{fontSize:11,color:d.closed?C.red:C.blue,fontWeight:600}}>
                  {d.closed ? "🚫 Closed all day" : `🕑 Special hours ${(d.start_time||"").slice(0,5)}–${(d.end_time||"").slice(0,5)}`}
                  {d.note ? ` · ${d.note}` : ""}
                </div>
              </div>
              <button onClick={()=>remove(d.id)} style={{padding:"5px 12px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function BusinessHoursEditor() {
  const [hours, setHours] = useState([]);
  const [location, setLocation] = useState("Beverly Hills");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { load(); }, [location]);

  const load = async () => {
    const { data } = await supabase.from("business_hours").select("*").eq("location", location).order("day_of_week");
    setHours(data || []);
  };

  const updateHour = (day_of_week, field, value) => {
    setHours(h => h.map(r => r.day_of_week === day_of_week ? {...r, [field]: value} : r));
  };

  const saveAll = async () => {
    setSaving(true);
    setMsg("");
    for (const h of hours) {
      await supabase.from("business_hours").update({
        is_open: h.is_open,
        open_time: h.open_time,
        close_time: h.close_time,
        updated_at: new Date().toISOString()
      }).eq("id", h.id);
    }
    setSaving(false);
    setMsg("Saved successfully!");
    setTimeout(() => setMsg(""), 2000);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Spa Operating Hours</div>
          <div style={{fontSize:11,color:C.muted}}>Configure when your spa is open for bookings</div>
        </div>
        <select value={location} onChange={e=>setLocation(e.target.value)} style={{...inp,width:"auto"}}>
          {LOCS.map(l => <option key={l}>{l}</option>)}
        </select>
      </div>
      {msg && <div style={{padding:"8px 12px",background:C.greenL,color:C.green,borderRadius:7,fontSize:12,fontWeight:600,marginBottom:12,border:"1px solid "+C.green+"40"}}>{msg}</div>}
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:10,overflow:"hidden"}}>
        {hours.map(h => (
          <div key={h.id} style={{padding:"12px 16px",borderBottom:"1px solid "+C.border,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <div style={{width:100,fontWeight:700,color:C.text,fontSize:13}}>{DAYS[h.day_of_week]}</div>
            <label style={{display:"flex",gap:6,alignItems:"center",cursor:"pointer"}}>
              <input type="checkbox" checked={h.is_open} onChange={e=>updateHour(h.day_of_week, "is_open", e.target.checked)} style={{accentColor:C.gold}} />
              <span style={{fontSize:12,color:h.is_open?C.green:C.muted,fontWeight:600}}>{h.is_open?"Open":"Closed"}</span>
            </label>
            {h.is_open && (<>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:11,color:C.muted}}>Open:</span>
                <input type="time" value={h.open_time||"09:00"} onChange={e=>updateHour(h.day_of_week,"open_time",e.target.value)} style={{...inp,width:110}} />
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:11,color:C.muted}}>Close:</span>
                <input type="time" value={h.close_time||"18:00"} onChange={e=>updateHour(h.day_of_week,"close_time",e.target.value)} style={{...inp,width:110}} />
              </div>
            </>)}
          </div>
        ))}
      </div>
      <button onClick={saveAll} disabled={saving} style={{marginTop:12,padding:"10px 20px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F,opacity:saving?0.6:1}}>{saving?"Saving...":"Save Hours"}</button>
    </div>
  );
}

export function ProviderAvailabilityEditor() {
  const [availability, setAvailability] = useState([]);
  const [timeoff, setTimeoff] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [showTimeOff, setShowTimeOff] = useState(false);
  const [newTimeOff, setNewTimeOff] = useState({start_date:"",end_date:"",reason:"",partial:false,start_time:"",end_time:""});

  // ★ Real providers from the DB (was a hardcoded demo list of fake names).
  const [PROVIDERS, setProviders] = useState([]);
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("providers").select("id,name,color").eq("active", true).order("order_index").order("name");
      const list = data || [];
      setProviders(list);
      setSelectedProvider(prev => prev ?? (list[0]?.id ?? null));
    })();
  }, []);

  useEffect(() => { if (selectedProvider != null) load(); }, [selectedProvider]);

  const load = async () => {
    const { data: avail } = await supabase.from("provider_availability").select("*").eq("provider_id", selectedProvider).order("day_of_week");
    setAvailability(avail || []);
    const { data: to } = await supabase.from("provider_timeoff").select("*").eq("provider_id", selectedProvider).order("start_date", { ascending: false });
    setTimeoff(to || []);
  };

  const updateDay = (dow, field, value) => {
    setAvailability(a => a.map(r => r.day_of_week === dow ? {...r, [field]: value} : r));
  };

  const saveAll = async () => {
    setSaving(true);
    for (const a of availability) {
      await supabase.from("provider_availability").update({
        is_working: a.is_working,
        start_time: a.start_time,
        end_time: a.end_time,
        updated_at: new Date().toISOString()
      }).eq("id", a.id);
    }
    setSaving(false);
    setMsg("Saved!");
    setTimeout(() => setMsg(""), 2000);
  };

  const addTimeOff = async () => {
    if (!newTimeOff.start_date) return;
    if (newTimeOff.partial && (!newTimeOff.start_time || !newTimeOff.end_time)) {
      alert("Enter both a start and end time for a partial-day block (or turn off “specific hours” for a full day).");
      return;
    }
    if (newTimeOff.partial && newTimeOff.end_time <= newTimeOff.start_time) {
      alert("End time must be after start time.");
      return;
    }
    const prov = PROVIDERS.find(p => p.id === selectedProvider);
    await supabase.from("provider_timeoff").insert([{
      provider_id: selectedProvider,
      provider_name: prov?.name,
      start_date: newTimeOff.start_date,
      // A single-day block just repeats the start date; a range uses end_date.
      end_date: newTimeOff.end_date || newTimeOff.start_date,
      reason: newTimeOff.reason || null,
      start_time: newTimeOff.partial ? newTimeOff.start_time : null,
      end_time: newTimeOff.partial ? newTimeOff.end_time : null,
    }]);
    setNewTimeOff({start_date:"",end_date:"",reason:"",partial:false,start_time:"",end_time:""});
    setShowTimeOff(false);
    load();
  };

  const removeTimeOff = async (id) => {
    if (!window.confirm("Remove this time off?")) return;
    await supabase.from("provider_timeoff").delete().eq("id", id);
    load();
  };

  const currentProvider = PROVIDERS.find(p => p.id === selectedProvider);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Provider Schedule & Availability</div>
          <div style={{fontSize:11,color:C.muted}}>When each provider is available for bookings</div>
        </div>
        <select value={selectedProvider ?? ""} onChange={e=>setSelectedProvider(+e.target.value)} style={{...inp,width:"auto",minWidth:200}}>
          {PROVIDERS.length === 0 && <option value="">Loading providers…</option>}
          {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      {msg && <div style={{padding:"8px 12px",background:C.greenL,color:C.green,borderRadius:7,fontSize:12,fontWeight:600,marginBottom:12,border:"1px solid "+C.green+"40"}}>{msg}</div>}
      <div style={{background:C.card,border:"1px solid "+C.border,borderLeft:"4px solid "+(currentProvider?.color||C.gold),borderRadius:10,overflow:"hidden",marginBottom:16}}>
        {availability.map(a => (
          <div key={a.id} style={{padding:"11px 16px",borderBottom:"1px solid "+C.border,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <div style={{width:100,fontWeight:700,color:C.text,fontSize:13}}>{DAYS[a.day_of_week]}</div>
            <label style={{display:"flex",gap:6,alignItems:"center",cursor:"pointer"}}>
              <input type="checkbox" checked={a.is_working} onChange={e=>updateDay(a.day_of_week,"is_working",e.target.checked)} style={{accentColor:C.gold}} />
              <span style={{fontSize:12,color:a.is_working?C.green:C.muted,fontWeight:600}}>{a.is_working?"Working":"Off"}</span>
            </label>
            {a.is_working && (<>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:11,color:C.muted}}>From:</span>
                <input type="time" value={a.start_time||"09:00"} onChange={e=>updateDay(a.day_of_week,"start_time",e.target.value)} style={{...inp,width:110}} />
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:11,color:C.muted}}>To:</span>
                <input type="time" value={a.end_time||"18:00"} onChange={e=>updateDay(a.day_of_week,"end_time",e.target.value)} style={{...inp,width:110}} />
              </div>
            </>)}
          </div>
        ))}
      </div>
      <button onClick={saveAll} disabled={saving} style={{padding:"10px 20px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F,opacity:saving?0.6:1,marginRight:8}}>{saving?"Saving...":"Save Schedule"}</button>

      <div style={{marginTop:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>Time Off / Vacations</div>
          <button onClick={()=>setShowTimeOff(true)} style={{padding:"6px 12px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>+ Add Time Off</button>
        </div>
        {timeoff.length === 0 && <div style={{padding:20,textAlign:"center",color:C.muted,fontSize:12,background:C.light,borderRadius:8,border:"1px dashed "+C.border}}>No scheduled time off</div>}
        {timeoff.map(t => (
          <div key={t.id} style={{padding:"10px 14px",background:C.light,borderRadius:8,marginBottom:6,display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>
                {t.start_date === t.end_date ? t.start_date : `${t.start_date} to ${t.end_date}`}
                {t.start_time && t.end_time
                  ? <span style={{marginLeft:8,fontSize:11,fontWeight:700,color:C.gold,background:C.goldBg,padding:"1px 7px",borderRadius:50}}>⏰ {t.start_time.slice(0,5)}–{t.end_time.slice(0,5)}</span>
                  : <span style={{marginLeft:8,fontSize:11,fontWeight:700,color:C.red,background:C.redL,padding:"1px 7px",borderRadius:50}}>All day</span>}
              </div>
              {t.reason && <div style={{fontSize:11,color:C.muted}}>{t.reason}</div>}
            </div>
            <button onClick={()=>removeTimeOff(t.id)} style={{padding:"5px 10px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>Remove</button>
          </div>
        ))}
      </div>

      {showTimeOff && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:C.surface,borderRadius:14,width:"100%",maxWidth:420,padding:22}}>
            <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4}}>Add Time Off</div>
            <div style={{fontSize:11,color:C.muted,marginBottom:14}}>Blocks new bookings for this provider. Leave “specific hours” off to block the whole day(s); turn it on to block only part of a day.</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>{newTimeOff.partial ? "Date" : "Start Date"}</label>
                <input type="date" value={newTimeOff.start_date} onChange={e=>setNewTimeOff({...newTimeOff,start_date:e.target.value})} style={inp} />
              </div>
              {!newTimeOff.partial && (
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>End Date <span style={{color:C.muted,fontWeight:400,textTransform:"none"}}>(same as start for one day)</span></label>
                  <input type="date" value={newTimeOff.end_date} onChange={e=>setNewTimeOff({...newTimeOff,end_date:e.target.value})} style={inp} />
                </div>
              )}
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"9px 12px",background:newTimeOff.partial?C.goldBg:C.light,border:"1px solid "+(newTimeOff.partial?C.borderGold:C.border),borderRadius:8}}>
                <input type="checkbox" checked={newTimeOff.partial} onChange={e=>setNewTimeOff({...newTimeOff,partial:e.target.checked})} style={{accentColor:C.gold}} />
                <span style={{fontSize:12,fontWeight:600,color:C.text}}>⏰ Block specific hours only (partial day)</span>
              </label>
              {newTimeOff.partial && (
                <div style={{display:"flex",gap:10}}>
                  <div style={{flex:1}}>
                    <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>From</label>
                    <input type="time" value={newTimeOff.start_time} onChange={e=>setNewTimeOff({...newTimeOff,start_time:e.target.value})} style={inp} />
                  </div>
                  <div style={{flex:1}}>
                    <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>To</label>
                    <input type="time" value={newTimeOff.end_time} onChange={e=>setNewTimeOff({...newTimeOff,end_time:e.target.value})} style={inp} />
                  </div>
                </div>
              )}
              <div>
                <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>Reason (optional)</label>
                <input value={newTimeOff.reason} onChange={e=>setNewTimeOff({...newTimeOff,reason:e.target.value})} style={inp} placeholder="Vacation, training, lunch, etc." />
              </div>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
              <button onClick={()=>setShowTimeOff(false)} style={{padding:"8px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
              <button onClick={addTimeOff} style={{padding:"8px 16px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── BLOCK A PROVIDER'S DATE/HOURS ──────────────────────────────────────────────
// Dead-simple, all-on-one-screen tool: pick a provider + a date, choose whole
// day or specific hours, and block it. Blocks apply to the public booking page
// AND the AI receptionist (no client can book that provider then).
export function ProviderBlockHours() {
  const [providers, setProviders] = useState([]);
  const [rows, setRows] = useState([]);
  const [extraRows, setExtraRows] = useState([]);
  const [tool, setTool] = useState("block"); // 'block' = block time · 'extra' = add date-specific availability
  const [f, setF] = useState({ provider_id:"", date:"", mode:"hours", start_time:"10:00", end_time:"14:00", reason:"" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}

  const load = async () => {
    const { data: provs } = await supabase.from("providers").select("id,name,color").eq("active", true).order("order_index").order("name");
    const list = provs || [];
    setProviders(list);
    setF(prev => prev.provider_id ? prev : { ...prev, provider_id: list[0]?.id ? String(list[0].id) : "" });
    const { data: to } = await supabase.from("provider_timeoff").select("*").order("start_date", { ascending: false });
    setRows(to || []);
    const { data: ex } = await supabase.from("provider_extra_availability").select("*").order("date");
    setExtraRows(ex || []);
  };
  useEffect(() => { load(); }, []);

  // Add a date-specific availability window (guest provider / extra day).
  const addExtra = async () => {
    if (!f.provider_id) { setMsg({ ok:false, text:"Pick a provider." }); return; }
    if (!f.date) { setMsg({ ok:false, text:"Pick a date." }); return; }
    if (!f.start_time || !f.end_time || f.end_time <= f.start_time) {
      setMsg({ ok:false, text:"Set a valid From/To time (To must be after From)." }); return;
    }
    setSaving(true); setMsg(null);
    const prov = providers.find(p => String(p.id) === String(f.provider_id));
    const { error } = await supabase.from("provider_extra_availability").insert([{
      provider_id: +f.provider_id,
      provider_name: prov?.name,
      date: f.date,
      start_time: f.start_time,
      end_time: f.end_time,
      note: f.reason || null,
    }]);
    setSaving(false);
    if (error) { setMsg({ ok:false, text:"Error: " + error.message }); return; }
    setMsg({ ok:true, text:`✅ ${prov?.name || "Provider"} is now bookable on ${f.date} from ${f.start_time}–${f.end_time} (booking page + AI).` });
    setF({ ...f, reason:"" });
    load();
  };

  const removeExtra = async (id) => {
    if (!window.confirm("Remove this extra availability? The provider won't be bookable then.")) return;
    await supabase.from("provider_extra_availability").delete().eq("id", id);
    load();
  };

  const add = async () => {
    if (!f.provider_id) { setMsg({ ok:false, text:"Pick a provider." }); return; }
    if (!f.date) { setMsg({ ok:false, text:"Pick a date." }); return; }
    if (f.mode === "hours" && (!f.start_time || !f.end_time || f.end_time <= f.start_time)) {
      setMsg({ ok:false, text:"Set a valid From/To time (To must be after From)." }); return;
    }
    setSaving(true); setMsg(null);
    const prov = providers.find(p => String(p.id) === String(f.provider_id));
    const { error } = await supabase.from("provider_timeoff").insert([{
      provider_id: +f.provider_id,
      provider_name: prov?.name,
      start_date: f.date,
      end_date: f.date,
      start_time: f.mode === "hours" ? f.start_time : null,
      end_time: f.mode === "hours" ? f.end_time : null,
      reason: f.reason || null,
    }]);
    setSaving(false);
    if (error) { setMsg({ ok:false, text:"Error: " + error.message }); return; }
    setMsg({ ok:true, text:`✅ Blocked ${prov?.name || "provider"} on ${f.date}${f.mode==="hours" ? ` from ${f.start_time}–${f.end_time}` : " (all day)"}. Clients & the AI can't book then.` });
    setF({ ...f, reason:"" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this block? Bookings will reopen for that time.")) return;
    await supabase.from("provider_timeoff").delete().eq("id", id);
    load();
  };

  const pName = (r) => r.provider_name || providers.find(p => p.id === r.provider_id)?.name || `Provider #${r.provider_id}`;
  const today = new Date().toISOString().split("T")[0];
  const upcoming = rows.filter(r => (r.end_date || r.start_date) >= today);
  const upcomingExtra = extraRows.filter(r => r.date >= today);

  return (
    <div>
      {/* Tool toggle: block time vs add extra date-specific availability */}
      <div style={{display:"flex",gap:8,marginBottom:12}}>
        {[["block","🚫 Block Time"],["extra","➕ Extra Days"]].map(([v,l])=>(
          <button key={v} onClick={()=>{ setTool(v); setMsg(null); }} style={{flex:1,padding:"11px",fontSize:13.5,fontWeight:800,cursor:"pointer",fontFamily:F,borderRadius:9,border:"1px solid "+(tool===v?C.gold:C.border),background:tool===v?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",color:tool===v?"#fff":C.text}}>{l}</button>
        ))}
      </div>
      {tool === "block" ? (
        <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Pick a provider and a date, then block the whole day or just specific hours (vacation, a half-day off, lunch, etc.). No one — clients on the booking link or the AI — can book that provider during a block.</div>
      ) : (
        <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Make a provider bookable on a SPECIFIC date & hours — perfect for a guest provider with no weekly schedule (e.g. someone covering vacation days). Add them in Providers first, then give them days here; they'll be bookable ONLY on these dates.</div>
      )}

      {/* The one-screen form */}
      <div style={{background:C.card,border:"1px solid "+C.borderGold,borderRadius:12,padding:18,marginBottom:20}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14,marginBottom:14}}>
          <div>
            <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>Provider</label>
            <select value={f.provider_id} onChange={e=>setF({...f,provider_id:e.target.value})} style={{...inp}}>
              {providers.length === 0 && <option value="">Loading…</option>}
              {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>Date</label>
            <input type="date" min={today} value={f.date} onChange={e=>setF({...f,date:e.target.value})} style={{...inp}} />
          </div>
        </div>

        {/* Whole day vs specific hours (block mode only) */}
        {tool === "block" && (
        <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
          {[["hours","⏰ Specific hours"],["day","🚫 Whole day"]].map(([v,l])=>(
            <button key={v} onClick={()=>setF({...f,mode:v})} style={{flex:1,minWidth:140,padding:"10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F,borderRadius:8,border:"1px solid "+(f.mode===v?C.gold:C.border),background:f.mode===v?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",color:f.mode===v?"#fff":C.text}}>{l}</button>
          ))}
        </div>
        )}

        {(tool === "extra" || f.mode === "hours") && (
          <div style={{display:"flex",gap:14,marginBottom:14,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:130}}>
              <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>From</label>
              <input type="time" value={f.start_time} onChange={e=>setF({...f,start_time:e.target.value})} style={{...inp}} />
            </div>
            <div style={{flex:1,minWidth:130}}>
              <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>To</label>
              <input type="time" value={f.end_time} onChange={e=>setF({...f,end_time:e.target.value})} style={{...inp}} />
            </div>
          </div>
        )}

        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:5,display:"block"}}>Reason (optional)</label>
          <input value={f.reason} onChange={e=>setF({...f,reason:e.target.value})} style={{...inp}} placeholder="Vacation, doctor appt, lunch…" />
        </div>

        {msg && <div style={{padding:"10px 14px",borderRadius:8,fontSize:12,fontWeight:600,marginBottom:12,background:msg.ok?C.greenL:C.redL,color:msg.ok?C.green:C.red,border:"1px solid "+(msg.ok?C.green:C.red)+"40"}}>{msg.text}</div>}

        {tool === "block" ? (
          <button onClick={add} disabled={saving} style={{width:"100%",padding:"13px",background:saving?C.light:"linear-gradient(135deg,"+C.red+",#a52f22)",color:saving?C.muted:"#fff",border:"none",borderRadius:9,fontSize:14,fontWeight:800,cursor:saving?"not-allowed":"pointer",fontFamily:F}}>
            {saving ? "Blocking…" : "🚫 Block This Time"}
          </button>
        ) : (
          <button onClick={addExtra} disabled={saving} style={{width:"100%",padding:"13px",background:saving?C.light:"linear-gradient(135deg,"+C.green+",#1e6b3f)",color:saving?C.muted:"#fff",border:"none",borderRadius:9,fontSize:14,fontWeight:800,cursor:saving?"not-allowed":"pointer",fontFamily:F}}>
            {saving ? "Adding…" : "➕ Make Available This Date"}
          </button>
        )}
      </div>

      {/* Upcoming extra days (guest availability) */}
      {tool === "extra" && (<>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>Upcoming Extra Days ({upcomingExtra.length})</div>
        {upcomingExtra.length === 0 ? (
          <div style={{padding:24,textAlign:"center",color:C.muted,fontSize:12,background:C.light,borderRadius:8,border:"1px dashed "+C.border}}>No extra days yet. Add one above.</div>
        ) : upcomingExtra.map(r => (
          <div key={r.id} style={{padding:"12px 14px",background:C.card,border:"1px solid "+C.border,borderLeft:"4px solid "+C.green,borderRadius:8,marginBottom:8,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:180}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text}}>{r.provider_name || providers.find(p=>p.id===r.provider_id)?.name || `Provider #${r.provider_id}`}</div>
              <div style={{fontSize:12,color:C.muted}}>
                {r.date}
                <span style={{marginLeft:8,fontWeight:700,color:C.green}}>✅ {String(r.start_time).slice(0,5)}–{String(r.end_time).slice(0,5)}</span>
                {r.note ? ` · ${r.note}` : ""}
              </div>
            </div>
            <button onClick={()=>removeExtra(r.id)} style={{padding:"6px 12px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>Remove</button>
          </div>
        ))}
      </>)}

      {/* Existing blocks */}
      {tool === "block" && (<>
      <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>Upcoming Blocks ({upcoming.length})</div>
      {upcoming.length === 0 ? (
        <div style={{padding:24,textAlign:"center",color:C.muted,fontSize:12,background:C.light,borderRadius:8,border:"1px dashed "+C.border}}>No blocks scheduled. Add one above.</div>
      ) : upcoming.map(r => (
        <div key={r.id} style={{padding:"12px 14px",background:C.card,border:"1px solid "+C.border,borderLeft:"4px solid "+(r.start_time?C.gold:C.red),borderRadius:8,marginBottom:8,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:180}}>
            <div style={{fontSize:13,fontWeight:700,color:C.text}}>{pName(r)}</div>
            <div style={{fontSize:12,color:C.muted}}>
              {r.start_date === r.end_date ? r.start_date : `${r.start_date} → ${r.end_date}`}
              {r.start_time && r.end_time
                ? <span style={{marginLeft:8,fontWeight:700,color:C.gold}}>⏰ {r.start_time.slice(0,5)}–{r.end_time.slice(0,5)}</span>
                : <span style={{marginLeft:8,fontWeight:700,color:C.red}}>All day</span>}
              {r.reason ? ` · ${r.reason}` : ""}
            </div>
          </div>
          <button onClick={()=>remove(r.id)} style={{padding:"6px 12px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>Remove</button>
        </div>
      ))}
      </>)}
    </div>
  );
}
